import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Restore as RestoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  addDoc,
  doc,
  query,
  where,
  orderBy,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import Swal from "sweetalert2";
import dayjs from "dayjs";

const DataRecoveryTool = () => {
  const [recoveryData, setRecoveryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchEmail, setSearchEmail] = useState("");
  const [recoveryDialogOpen, setRecoveryDialogOpen] = useState(false);
  const [selectedRecovery, setSelectedRecovery] = useState(null);
  const [recoveryProgress, setRecoveryProgress] = useState(0);

  useEffect(() => {
    loadRecoveryData();
  }, []);

  const loadRecoveryData = async () => {
    setLoading(true);
    try {
      // Get all users to analyze their data
      const usersQuery = query(collection(DATABASE, "users"), orderBy("registered_date", "desc"));
      const usersSnapshot = await getDocs(usersQuery);
      
      const recoveryAnalysis = await Promise.all(
        usersSnapshot.docs.map(async (userDoc) => {
          const userData = userDoc.data();
          const userId = userDoc.id;
          
          // Check for missing performance data
          const ledgersQuery = query(
            collection(DATABASE, "ledgers"),
            where("user_id", "==", userId),
            where("transection_type", "==", 3)
          );
          const ledgersSnapshot = await getDocs(ledgersQuery);
          
          const transactionsQuery = query(
            collection(DATABASE, "transection_requests"),
            where("user_id", "==", userId),
            where("request_type", "==", 2)
          );
          const transactionsSnapshot = await getDocs(transactionsQuery);
          
          // Check if user has any performance data
          const hasPerformanceData = ledgersSnapshot.size > 0 || transactionsSnapshot.size > 0;
          
          // Check monthly closures to see what should exist
          const monthlyClosingsQuery = query(
            collection(DATABASE, "monthly_closings"),
            where("user_id", "==", userId),
            orderBy("date", "desc")
          );
          const monthlyClosingsSnapshot = await getDocs(monthlyClosingsQuery);
          
          const expectedClosures = monthlyClosingsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            month: doc.data().closing_month,
            year: doc.data().closing_year,
            percent: doc.data().percent,
            profit_loss: doc.data().profit_loss,
            date: doc.data().date?.toDate?.() || new Date(),
          }));
          
          return {
            userId,
            userEmail: userData.email,
            userName: userData.full_name,
            currentBalance: userData.net_balance,
            hasPerformanceData,
            expectedClosures,
            missingPerformanceCount: expectedClosures.length - ledgersSnapshot.size,
            status: hasPerformanceData ? "Has Data" : "Missing Data"
          };
        })
      );
      
      // Filter to only show users with missing data
      const missingDataUsers = recoveryAnalysis.filter(user => 
        user.expectedClosures.length > 0 && !user.hasPerformanceData
      );
      
      setRecoveryData(missingDataUsers);
    } catch (error) {
      console.error("Error loading recovery data:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load recovery data",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredData = recoveryData.filter((item) => {
    const emailMatch = !searchEmail || 
      item.userEmail?.toLowerCase().includes(searchEmail.toLowerCase());
    return emailMatch;
  });

  const handleRecoverUserData = (user) => {
    setSelectedRecovery(user);
    setRecoveryDialogOpen(true);
  };

  const confirmRecovery = async () => {
    if (!selectedRecovery) return;

    setRecoveryProgress(0);
    try {
      const batch = writeBatch(DATABASE);
      const recoverySteps = [];

      // Recreate performance transactions for each expected closure
      for (let i = 0; i < selectedRecovery.expectedClosures.length; i++) {
        const closure = selectedRecovery.expectedClosures[i];
        setRecoveryProgress((i / selectedRecovery.expectedClosures.length) * 80);

        // Create ledger entry
        const ledgerId = `${selectedRecovery.userId}_${dayjs(closure.date).format("YYYYMMDDHHmmss")}`;
        const ledgerRef = doc(DATABASE, "ledgers", ledgerId);
        
        batch.set(ledgerRef, {
          user_id: selectedRecovery.userId,
          transection_type: 3, // Performance type
          profit_loss_balance: closure.profit_loss,
          profit_loss_percent: closure.percent,
          date: Timestamp.fromDate(closure.date),
          created_at: Timestamp.fromDate(closure.date), // Use closure date, not current time
          doc_id: ledgerId,
          new_txn_amount: closure.profit_loss,
          previous_balance: selectedRecovery.currentBalance - closure.profit_loss,
          current_balance: selectedRecovery.currentBalance,
          recovery_note: "Recovered from monthly closure data"
        });
        
        recoverySteps.push(`Created ledger entry for ${dayjs().month(closure.month).year(closure.year).format("MMMM YYYY")}`);

        // Create transaction request
        const transactionId = `${selectedRecovery.userId}_${dayjs(closure.date).format("YYYYMMDDHHmmss")}`;
        const transactionRef = doc(DATABASE, "transection_requests", transactionId);
        
        batch.set(transactionRef, {
          user_id: selectedRecovery.userId,
          request_type: 2, // Performance type
          request_status: 5, // Approved
          profit_loss: closure.profit_loss,
          profit_loss_percent: closure.percent,
          date: Timestamp.fromDate(closure.date),
          request_approval_time: Timestamp.fromDate(closure.date),
          created_at: Timestamp.fromDate(closure.date), // Use closure date for proper sorting
          txn_id: Number(dayjs(closure.date).format("YYYYMMDDHHmmss")),
          doc_id: transactionId,
          recovery_note: "Recovered from monthly closure data"
        });
        
        recoverySteps.push(`Created transaction entry for ${dayjs().month(closure.month).year(closure.year).format("MMMM YYYY")}`);
      }

      // Execute all operations
      setRecoveryProgress(90);
      await batch.commit();

      setRecoveryProgress(100);

      Swal.fire({
        icon: "success",
        title: "Data Recovery Successful!",
        html: `
          <div>
            <p><strong>Recovered data for:</strong></p>
            <p>User: ${selectedRecovery.userEmail}</p>
            <p>Closures recovered: ${selectedRecovery.expectedClosures.length}</p>
            <br>
            <p><strong>Operations performed:</strong></p>
            <ul style="text-align: left; font-size: 12px;">
              ${recoverySteps.map(step => `<li>${step}</li>`).join('')}
            </ul>
          </div>
        `,
        width: 600,
      });

      setRecoveryDialogOpen(false);
      setSelectedRecovery(null);
      loadRecoveryData();
    } catch (error) {
      console.error("Error during recovery:", error);
      Swal.fire({
        icon: "error",
        title: "Recovery Failed",
        text: `Failed to recover data: ${error.message}`,
      });
    } finally {
      setRecoveryProgress(0);
    }
  };

  const getStatusChip = (user) => {
    if (user.missingPerformanceCount > 0) {
      return <Chip label={`Missing ${user.missingPerformanceCount} entries`} color="error" size="small" />;
    } else if (user.hasPerformanceData) {
      return <Chip label="Has Data" color="success" size="small" />;
    } else {
      return <Chip label="No Closures" color="default" size="small" />;
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Data Recovery Tool
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Recover missing performance transactions and ledger entries based on monthly closure data.
        This tool will recreate the performance data that was accidentally deleted.
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {recoveryData.length}
              </Typography>
              <Typography variant="body2">Users with Missing Data</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error.main">
                {recoveryData.reduce((sum, user) => sum + user.missingPerformanceCount, 0)}
              </Typography>
              <Typography variant="body2">Missing Performance Entries</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {recoveryData.filter(user => user.expectedClosures.length > 0).length}
              </Typography>
              <Typography variant="body2">Users with Closures</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {recoveryData.filter(user => user.hasPerformanceData).length}
              </Typography>
              <Typography variant="body2">Users with Data</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          label="Search by Email"
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        />
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={loadRecoveryData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Recovery Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Current Balance</TableCell>
              <TableCell>Expected Closures</TableCell>
              <TableCell>Missing Entries</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No users with missing data found
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((user) => (
                <TableRow key={user.userId}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {user.userEmail}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.userName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      ${user.currentBalance?.toFixed(2) || "0.00"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.expectedClosures.length}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="error.main" fontWeight="bold">
                      {user.missingPerformanceCount}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(user)}</TableCell>
                  <TableCell>
                    <Tooltip title="Recover missing performance data">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleRecoverUserData(user)}
                        disabled={user.missingPerformanceCount === 0}
                      >
                        <RestoreIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Recovery Confirmation Dialog */}
      <Dialog open={recoveryDialogOpen} onClose={() => setRecoveryDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RestoreIcon color="primary" />
            Confirm Data Recovery
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRecovery && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  This will recreate missing performance transactions and ledger entries based on monthly closure data.
                </Typography>
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                Recovery Details:
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>User:</strong> {selectedRecovery.userEmail}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Current Balance:</strong> ${selectedRecovery.currentBalance?.toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Expected Closures:</strong> {selectedRecovery.expectedClosures.length}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2"><strong>Missing Entries:</strong> {selectedRecovery.missingPerformanceCount}</Typography>
                </Grid>
              </Grid>

              <Typography variant="body2" gutterBottom>
                <strong>Closures to recover:</strong>
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ccc', p: 1, borderRadius: 1 }}>
                {selectedRecovery.expectedClosures.map((closure, index) => (
                  <Typography key={index} variant="caption" display="block">
                    {dayjs().month(closure.month).year(closure.year).format("MMMM YYYY")}: {closure.percent?.toFixed(2)}% (${closure.profit_loss?.toFixed(2)})
                  </Typography>
                ))}
              </Box>

              {recoveryProgress > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Recovery Progress: {recoveryProgress}%
                  </Typography>
                  <CircularProgress variant="determinate" value={recoveryProgress} />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecoveryDialogOpen(false)} disabled={recoveryProgress > 0}>
            Cancel
          </Button>
          <Button 
            onClick={confirmRecovery} 
            color="primary" 
            variant="contained"
            disabled={recoveryProgress > 0}
            startIcon={<RestoreIcon />}
          >
            {recoveryProgress > 0 ? "Recovering..." : "Confirm Recovery"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataRecoveryTool;
